import { create } from "zustand";
import type {
  ChatMessage,
  VerseReference,
} from "@/lib/types";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

type JsonRecord = Record<string, unknown>;

function asRecord(value: unknown): JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};
}

function createId(): string {
  const cryptoObject = globalThis.crypto;

  if (
    cryptoObject &&
    typeof cryptoObject.randomUUID === "function"
  ) {
    return cryptoObject.randomUUID();
  }

  if (
    cryptoObject &&
    typeof cryptoObject.getRandomValues === "function"
  ) {
    const bytes = cryptoObject.getRandomValues(new Uint8Array(16));

    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");

    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join("-");
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeSources(value: unknown): VerseReference[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      const row = asRecord(item);
      const book = typeof row.book === "string" ? row.book : "";

      if (!book) {
        return null;
      }

      return {
        book,
        chapter: Number(row.chapter ?? 0),
        verse: Number(row.verse ?? 0),
        text: typeof row.text === "string" ? row.text : "",
        translation:
          typeof row.translation === "string"
            ? row.translation
            : undefined,
      };
    })
    .filter(
      (verse): verse is VerseReference => verse !== null,
    );
}

interface ChatStore {
  messages: ChatMessage[];
  isStreaming: boolean;
  currentSources: VerseReference[];
  sendMessage: (
    text: string,
    translation?: string,
  ) => Promise<void>;
  clearChat: () => void;
}

export const useChatStore = create<ChatStore>((set) => ({
  messages: [],
  isStreaming: false,
  currentSources: [],

  sendMessage: async (
    text: string,
    translation = "KJV",
  ) => {
    const userMessage: ChatMessage = {
      id: createId(),
      role: "user",
      content: text,
      timestamp: Date.now(),
    };

    const assistantId = createId();

    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: Date.now(),
    };

    set((state) => ({
      messages: [
        ...state.messages,
        userMessage,
        assistantMessage,
      ],
      isStreaming: true,
      currentSources: [],
    }));

    try {
      const response = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: text,
          translation: translation.toUpperCase(),
        }),
      });

      if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(
          `Chat failed ${response.status}: ${
            detail || response.statusText
          }`,
        );
      }

      if (!response.body) {
        throw new Error("Chat response did not contain a stream.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();

          if (!trimmed.startsWith("data:")) {
            continue;
          }

          const rawData = trimmed.slice(5).trim();

          if (!rawData || rawData === "[DONE]") {
            continue;
          }

          try {
            const event = asRecord(JSON.parse(rawData));
            const type =
              typeof event.type === "string"
                ? event.type
                : "";

            if (
              type === "token" &&
              typeof event.data === "string"
            ) {
              const token = event.data;

              set((state) => ({
                messages: state.messages.map((message) =>
                  message.id === assistantId
                    ? {
                        ...message,
                        content: message.content + token,
                      }
                    : message,
                ),
              }));
            }

            if (type === "sources") {
              const sources = normalizeSources(event.data);

              set((state) => ({
                messages: state.messages.map((message) =>
                  message.id === assistantId
                    ? { ...message, verses: sources }
                    : message,
                ),
                currentSources: sources,
              }));
            }
          } catch {
            // Ignore malformed stream events.
          }
        }
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unknown chat error";

      set((state) => ({
        messages: state.messages.map((entry) =>
          entry.id === assistantId
            ? {
                ...entry,
                content: `Sorry, the chat request failed: ${message}`,
              }
            : entry,
        ),
      }));
    } finally {
      set({ isStreaming: false });
    }
  },

  clearChat: () =>
    set({
      messages: [],
      currentSources: [],
      isStreaming: false,
    }),
}));
